import React, { useState, useRef } from 'react';
import { Check, ArrowRight, Eye, EyeSlash, CircleNotch, WarningCircle, CheckCircle } from '@phosphor-icons/react';

const StartTrial = () => {
  const [form, setForm] = useState({ institute_name: '', subdomain: '', college_code: '', admin_name: '', admin_email: '', admin_password: '', phone: '', address: '', billing_address: '', billing_city: '', billing_state: '', billing_pincode: '', gst_number: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if coming from purchase (paid)
  const searchParams = new URLSearchParams(window.location.search);
  const paidPlan = searchParams.get('plan');
  const paidBilling = searchParams.get('billing');
  const paymentId = searchParams.get('payment_id');
  const isPaid = !!(paidPlan && paymentId);

  // Availability status per field
  const [availability, setAvailability] = useState({
    subdomain: { checking: false, available: null, reason: null },
    college_code: { checking: false, available: null, reason: null },
    admin_email: { checking: false, available: null, reason: null },
  });
  const debounceRefs = useRef({ subdomain: null, college_code: null, admin_email: null });

  // Pre-fill signup + billing from query params if coming from Purchase flow
  React.useEffect(() => {
    if (isPaid) {
      const preload = {};
      ['institute_name', 'subdomain', 'college_code', 'admin_name', 'admin_email', 'admin_password', 'phone',
       'billing_address', 'billing_city', 'billing_state', 'billing_pincode', 'gst_number'].forEach(k => {
        const v = searchParams.get(k);
        if (v) preload[k] = v;
      });
      if (Object.keys(preload).length) setForm(f => ({ ...f, ...preload }));
    }
  }, []); // eslint-disable-line

  // Debounced availability check
  const checkField = (field, value) => {
    if (debounceRefs.current[field]) clearTimeout(debounceRefs.current[field]);
    if (!value || value.trim().length < 2) {
      setAvailability(a => ({ ...a, [field]: { checking: false, available: null, reason: null } }));
      return;
    }
    setAvailability(a => ({ ...a, [field]: { checking: true, available: null, reason: null } }));
    debounceRefs.current[field] = setTimeout(async () => {
      try {
        const body = {};
        if (field === 'admin_email') body.email = value;
        else body[field] = value;
        const res = await fetch(`${API_URL}/api/public/check-availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const key = field === 'admin_email' ? 'email' : field;
        const status = data[key] || {};
        setAvailability(a => ({ ...a, [field]: { checking: false, available: status.available, reason: status.reason } }));
      } catch {
        setAvailability(a => ({ ...a, [field]: { checking: false, available: null, reason: 'Could not verify' } }));
      }
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Block submit if any field is known-unavailable
    for (const f of ['subdomain', 'college_code', 'admin_email']) {
      if (availability[f].available === false) {
        setError(availability[f].reason || 'Please fix the highlighted fields.');
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...form, admin_email: (form.admin_email || '').trim().toLowerCase() };
      if (isPaid) {
        payload.plan = paidPlan;
        payload.billing_cycle = paidBilling;
        payload.payment_id = paymentId;
      }
      const res = await fetch(`${API_URL}/api/public/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Trial signup failed');
      setSuccess(data);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('tenant_code', data.subdomain);
      localStorage.setItem('user', JSON.stringify({ role: 'Admin', tenant_id: data.tenant_id, full_name: form.admin_name, email: payload.admin_email }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubdomain = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setForm({ ...form, subdomain: clean });
    checkField('subdomain', clean);
  };

  const handleCollegeCode = (val) => {
    const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setForm({ ...form, college_code: clean });
    checkField('college_code', clean);
  };

  const handleEmail = (val) => {
    const clean = val.trim();
    setForm({ ...form, admin_email: clean });
    checkField('admin_email', clean);
  };

  const FieldStatus = ({ field }) => {
    const s = availability[field];
    if (s.checking) return <span className="text-[11px] text-gray-400 flex items-center gap-1"><CircleNotch size={11} className="animate-spin" /> Checking…</span>;
    if (s.available === true) return <span className="text-[11px] text-green-600 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Available</span>;
    if (s.available === false) return <span className="text-[11px] text-red-600 flex items-center gap-1"><WarningCircle size={11} weight="fill" /> {s.reason}</span>;
    return null;
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-xl border border-gray-100">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0a1628] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Trial Activated!</h2>
          <p className="text-gray-500 mb-6">Your 14-day free trial is active. All premium features are unlocked.</p>
          <div className="bg-gray-50 rounded-xl p-5 text-left mb-6 space-y-3">
            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
              <span className="text-sm text-gray-500">Login URL</span>
              <a href={`https://cloudcampus.site/${success.subdomain}/login`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-blue-600 underline">cloudcampus.site/{success.subdomain}/login</a>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
              <span className="text-sm text-gray-500">Login ID (Email)</span>
              <strong className="text-sm text-[#0a1628]">{form.admin_email}</strong>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
              <span className="text-sm text-gray-500">Password</span>
              <strong className="text-sm text-[#0a1628]">{form.admin_password}</strong>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Trial Ends</span>
              <strong className="text-sm text-red-600">{success.trial_ends}</strong>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            Save these credentials! You will also receive them via email.
          </p>
          <button onClick={() => window.location.href = `/${success.subdomain}/login`} className="w-full py-3.5 bg-[#0a1628] text-white rounded-xl font-medium hover:bg-[#1a2a42] transition flex items-center justify-center gap-2" data-testid="go-to-dashboard">
            Go to Login <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/cloudcampus-logo.png" alt="CloudCampus" className="h-12" />
          </div>
          <h1 className="text-3xl font-bold text-[#0a1628] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{isPaid ? 'Setup Your Campus' : 'Start Your 14-Day Free Trial'}</h1>
          <p className="text-gray-500">{isPaid ? 'Payment received! Fill in your institute details to activate your panel.' : 'Full access to all premium features. No credit card required.'}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-4" data-testid="trial-form">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="trial-error">{error}</div>}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institute Name *</label>
            <input type="text" required value={form.institute_name} onChange={e => setForm({...form, institute_name: e.target.value})} placeholder="e.g. Modern College of Education" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-institute" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain *</label>
              <input type="text" required value={form.subdomain} onChange={e => handleSubdomain(e.target.value)} placeholder="e.g. moderncollege" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-subdomain" />
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-400">cloudcampus.site/{form.subdomain || '...'}</p>
                <FieldStatus field="subdomain" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">College Code *</label>
              <input type="text" required value={form.college_code} onChange={e => handleCollegeCode(e.target.value)} placeholder="e.g. MCE" maxLength={10} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none uppercase" data-testid="trial-code" />
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-400">Used in Student IDs</p>
                <FieldStatus field="college_code" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name *</label>
            <input type="text" required value={form.admin_name} onChange={e => setForm({...form, admin_name: e.target.value})} placeholder="Your full name" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={form.admin_email} onChange={e => handleEmail(e.target.value)} placeholder="you@email.com" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-email" />
              <div className="mt-1 flex items-center justify-end">
                <FieldStatus field="admin_email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 9876543210" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-phone" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} required minLength={6} value={form.admin_password} onChange={e => setForm({...form, admin_password: e.target.value})} placeholder="Min 6 characters" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none pr-10" data-testid="trial-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address (Optional)</label>
            <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Institute address" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" />
          </div>

          {/* GST Billing Details (required for paid, optional for trial) */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#0a1628]">Billing Details {isPaid ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(Optional for trial)</span>}</h3>
            </div>
            <div className="space-y-3">
              <input type="text" {...(isPaid ? { required: true } : {})} value={form.billing_address} onChange={e => setForm({...form, billing_address: e.target.value})} placeholder="Billing address" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-billing-address" />
              <div className="grid grid-cols-2 gap-3">
                <input type="text" {...(isPaid ? { required: true } : {})} value={form.billing_city} onChange={e => setForm({...form, billing_city: e.target.value})} placeholder="City" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-billing-city" />
                <input type="text" {...(isPaid ? { required: true } : {})} value={form.billing_state} onChange={e => setForm({...form, billing_state: e.target.value})} placeholder="State" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-billing-state" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="text" maxLength={6} value={form.billing_pincode} onChange={e => setForm({...form, billing_pincode: e.target.value.replace(/\D/g, '')})} placeholder="PIN code" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none" data-testid="trial-billing-pincode" />
                <input type="text" maxLength={15} value={form.gst_number} onChange={e => setForm({...form, gst_number: e.target.value.toUpperCase()})} placeholder="GST Number (optional)" className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1628] outline-none uppercase" data-testid="trial-gst-number" />
              </div>
              <p className="text-xs text-gray-400">GST Number helps generate compliant tax invoice for Input Tax Credit (ITC).</p>
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full py-3.5 bg-[#dc2626] text-white rounded-xl font-medium hover:bg-[#b91c1c] transition disabled:opacity-50 flex items-center justify-center gap-2" data-testid="trial-submit">
            {loading ? 'Creating your campus...' : (isPaid ? 'Activate Panel' : 'Start 14-Day Free Trial')} {!loading && <ArrowRight size={18} />}
          </button>

          <div className="text-center text-xs text-gray-400 mt-4 space-y-1">
            <p>By signing up, you agree to our Terms of Service</p>
            <p className="flex items-center justify-center gap-4 mt-2">
              <span className="flex items-center gap-1"><Check size={12} className="text-green-500" /> No credit card</span>
              <span className="flex items-center gap-1"><Check size={12} className="text-green-500" /> All features</span>
              <span className="flex items-center gap-1"><Check size={12} className="text-green-500" /> 14 days free</span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartTrial;
